import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTest } from "@/hooks/useTest";
import { VehicleTest } from "@/types/vehicleTest";
import { ActiveTest } from "@/admin/test/ActiveTest";
import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useTheme } from "@/context/ThemeContext";
import CreateTestButton from "@/components/car-test/CreateTestButton";
import {
  CarTestSortSelect,
  type CarTestListSort,
} from "@/components/car-test/CarTestSortSelect";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VehicleTestDocumentView } from "@/admin/pages/VehicleTestDocumentView";
import { generateVehicleTestPdf } from "@/utils/pdfGenerator";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { Pagination } from "@/components/ui/pagination";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useNativeSheetOpensExpanded } from "@/lib/native/useNativeSheetOpensExpanded";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { Archive, ListChecks, Check } from "lucide-react";
import { MobilePillTabs } from "@/components/ui/mobile-pill-tabs";

function mapVehicleTestToDocumentData(test: VehicleTest) {
  return test; // Return the test object directly since VehicleTestDocumentView expects VehicleTest
}

const CarTest = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [selectedTestForCompletion, setSelectedTestForCompletion] =
    useState<VehicleTest | null>(null);
  const [isRequestChangeDialogOpen, setIsRequestChangeDialogOpen] =
    useState(false);
  const [selectedTestForChange, setSelectedTestForChange] =
    useState<VehicleTest | null>(null);
  const [requestChangeMessage, setRequestChangeMessage] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTestForEdit, setSelectedTestForEdit] =
    useState<VehicleTest | null>(null);
  const { theme } = useTheme();
  const { user } = useAuth();
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { isNative: isNativeApp } = useNativePlatform();
  const [search, setSearch] = useState("");
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  useNativeSheetOpensExpanded(!!viewTest, isNativeApp);
  const navigate = useNavigate();
  const location = useLocation();

  // Get tab from URL parameter: "own" | "submitted"
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const tabFromUrl = searchParams.get("tab") || "own";
  const [activeTab, setActiveTab] = useState<"own" | "submitted">(
    tabFromUrl === "submitted" ? "submitted" : "own",
  );
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isArchiveMode, setIsArchiveMode] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:carTests",
    10,
    [10, 25, 50],
  );
  const [searchQuery, setSearchQuery] = useState(""); // Debounced search query
  const [searchInput, setSearchInput] = useState(""); // Immediate search input
  const [testSort, setTestSort] = useState<CarTestListSort>("-updatedAt");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update active tab when URL parameter changes (dealer only)
  useEffect(() => {
    if (tabFromUrl === "submitted") {
      setActiveTab("submitted");
    } else {
      setActiveTab("own");
    }
    setCurrentPage(1); // Reset to first page when tab changes
  }, [tabFromUrl]);

  const [activeTests, setActiveTests] = useState<VehicleTest[] | null>(null);
  const [submittedTests, setSubmittedTests] = useState<VehicleTest[] | null>(
    null,
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<{
    isOpen: boolean;
    testName?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });

  // Use the test hook to fetch user's own tests
  // Note: activeTab is only for frontend filtering, not for API queries
  const {
    userTests,
    totalUserTests,
    isLoadingUserTests,
    userTestsError,
    refetchUserTests,
    archiveCarTests,
    isArchivingCarTests,
    withdrawPendingReview,
    requestTestChange,
  } = useTest({
    disableAdminQuery: true,
    page: currentPage,
    limit: itemsPerPage,
    search: searchQuery || undefined,
    sort: testSort,
  });

  // Search is now handled server-side, no need for client-side filtering

  // useEffect to split tests into Mina tester vs Inskickade
  useEffect(() => {
    if (userTests && userTests.length > 0) {
      // Mina tester: work-in-progress / needs user action
      const own = userTests.filter(
        (test) =>
          test.status === "ongoing" ||
          test.status === "complement" ||
          test.status === "change-requested",
      );
      setActiveTests(own.length > 0 ? own : null);

      // Inskickade: under review or decided / listing created
      const submitted = userTests.filter(
        (test) =>
          test.status === "pending-review" ||
          test.status === "approved" ||
          test.status === "rejected" ||
          test.status === "created" ||
          test.status === "active-listing" ||
          test.status === "completed",
      );
      setSubmittedTests(submitted.length > 0 ? submitted : null);
    } else {
      setActiveTests(null);
      setSubmittedTests(null);
    }
  }, [userTests]);

  const deepLinkGenRef = useRef(0);

  // Open a specific test from e.g. email: /car-test?id=<carTestId>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (!id) return;
    if (isLoadingUserTests || userTests === undefined) return;

    const myGen = ++deepLinkGenRef.current;
    let cancelled = false;

    (async () => {
      try {
        let test: VehicleTest | undefined = userTests.find((t) => t.id === id);
        if (!test) {
          const { TestService } = await import("@/services/test/testService");
          const fetched = await TestService.getTestById(id);
          test = fetched ?? undefined;
        }
        if (cancelled || myGen !== deepLinkGenRef.current) return;

        if (!test) {
          toast.error(ct.messages.testNotFoundViaLink);
          const next = new URLSearchParams(location.search);
          next.delete("id");
          navigate(
            { pathname: location.pathname, search: next.toString() },
            { replace: true },
          );
          return;
        }

        const ownStatuses = [
          "ongoing",
          "complement",
          "change-requested",
        ];
        const wantTab = ownStatuses.includes(test.status)
          ? "own"
          : "submitted";
        if (wantTab !== activeTab) {
          setActiveTab(wantTab);
          navigate(`/car-test?tab=${wantTab}&id=${encodeURIComponent(id)}`, {
            replace: true,
          });
          return;
        }

        if (cancelled || myGen !== deepLinkGenRef.current) return;

        const next = new URLSearchParams(location.search);
        next.delete("id");
        const qs = next.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : "" },
          { replace: true },
        );

        setSelectedTestForEdit(test);
        setIsEditDialogOpen(true);
      } catch (e) {
        if (myGen !== deepLinkGenRef.current) return;
        toast.error(ct.messages.testNotFoundViaLink);
        const next = new URLSearchParams(location.search);
        next.delete("id");
        navigate(
          { pathname: location.pathname, search: next.toString() },
          { replace: true },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    location.pathname,
    isLoadingUserTests,
    userTests,
    activeTab,
    navigate,
    ct.messages.testNotFoundViaLink,
  ]);

  const handleCreateTest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCompleteTest = (test: VehicleTest) => {
    setSelectedTestForCompletion(test);
    setIsCompleteDialogOpen(true);
  };

  const handleRequestChange = (test: VehicleTest) => {
    setSelectedTestForChange(test);
    setRequestChangeMessage("");
    setIsRequestChangeDialogOpen(true);
  };

  const handleWithdrawReview = async (test: VehicleTest) => {
    try {
      await withdrawPendingReview(test.id);
      toast.success(ct.messages.withdrawReviewSuccess);
    } catch {
      toast.error(ct.messages.withdrawReviewError);
    }
  };

  const handleSendChangeRequest = async () => {
    if (!selectedTestForChange || !requestChangeMessage.trim()) return;

    try {
      await requestTestChange({
        testId: selectedTestForChange.id,
        message: requestChangeMessage,
      });
      toast.success(ct.messages.changeRequestSent);
      setIsRequestChangeDialogOpen(false);
      setSelectedTestForChange(null);
      setRequestChangeMessage("");
    } catch {
      toast.error(ct.messages.changeRequestError);
    }
  };

  const handleEdit = (test: VehicleTest) => {
    setSelectedTestForEdit(test);
    setIsEditDialogOpen(true);
  };

  // Show loading state
  if (isLoadingUserTests || userTests === undefined) {
    return (
      <div className="container max-w-4xl mx-auto px-4 space-y-6 relative">
        {!isMobile && (
          <div className="flex items-center gap-3 mb-2">
            <BtnNavigateBack label="Dashboard" className="mb-0 shrink-0" />
          </div>
        )}
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">{ct.messages.loadingTests}</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (userTestsError) {
    return (
      <div className="container max-w-4xl mx-auto px-4 space-y-6 relative">
        {!isMobile && (
          <div className="flex items-center gap-3 mb-2">
            <BtnNavigateBack label="Dashboard" className="mb-0 shrink-0" />
          </div>
        )}
        <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-500">
              {ct.messages.errorLoading}
              <button
                onClick={() => refetchUserTests()}
                className="ml-2 underline"
              >
                {ct.messages.tryAgain}
              </button>
            </div>
          </div>
      </div>
    );
  }

  const handleDownloadPdf = async (test: VehicleTest) => {
    setIsGeneratingPdf({
      isOpen: true,
      testName: test.vehicleName,
      fileName: `fordonsprotokoll-${test.vehicleDetails?.regNumber || test.id}.pdf`,
    });
    await new Promise((r) => setTimeout(r, 100));
    try {
      await generateVehicleTestPdf(test, language);
      toast.success(ct.messages.pdfGenerated);
    } catch (error) {
      toast.error("Fel vid PDF-generering");
    } finally {
      await new Promise((r) => setTimeout(r, 300));
      setIsGeneratingPdf({ isOpen: false });
    }
  };

  const toggleArchiveMode = () => {
    setIsArchiveMode(!isArchiveMode);
    setSelectedTests(new Set());
  };

  const archiveToggleLabel = (submittedTab?: boolean) => {
    if (isArchiveMode) {
      if (submittedTab) return ct.archive.exitUnarchiveMode;
      return isMobile ? ct.actions.exitArchiveMobile : ct.actions.exitArchive;
    }
    return isMobile ? ct.actions.archiveMobile : ct.actions.archive;
  };

  const renderArchiveNavButton = (opts?: {
    fullWidth?: boolean;
    compact?: boolean;
  }) => (
    <ColoredOutlineButton
      type="button"
      variant="blue"
      tintedBackground
      size="sm"
      onClick={() => navigate("/car-test/archive")}
      className={cn(
        "justify-center whitespace-nowrap gap-1",
        opts?.compact
          ? "h-7 px-2 text-[11px]"
          : "h-8 px-2.5 text-xs gap-1.5",
        opts?.fullWidth ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
      )}
    >
      <Archive
        className={opts?.compact ? "h-3 w-3 shrink-0" : OUTLINE_TEXT_BUTTON_ICON_CLASS}
        strokeWidth={2.35}
      />
      <span className="truncate">
        {isMobile ? ct.archive.buttonLabel : ct.archive.title}
      </span>
    </ColoredOutlineButton>
  );

  /** Compact secondary archive actions (mobile). */
  const renderMobileArchiveBar = () => {
    const submittedTab = activeTab === "submitted";
    const showConfirm = isArchiveMode && selectedTests.size > 0;
    const compactBtn =
      "h-7 px-2 text-[11px] gap-1 justify-center whitespace-nowrap flex-1 min-w-0";

    return (
      <div className="relative mb-2 min-h-7">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-[1]">
          <CreateTestButton onClick={handleCreateTest} />
        </div>
        <div className="flex gap-1.5 items-stretch pr-[4.5rem]">
          <ColoredOutlineButton
            type="button"
            variant="neutral"
            tintedBackground
            size="sm"
            onClick={toggleArchiveMode}
            className={compactBtn}
          >
            <ListChecks
              className="h-3 w-3 shrink-0"
              strokeWidth={2.35}
            />
            <span className="truncate">{archiveToggleLabel(submittedTab)}</span>
          </ColoredOutlineButton>

          {showConfirm ? (
            <ColoredOutlineButton
              type="button"
              variant="success"
              tintedBackground
              size="sm"
              onClick={() => setShowArchiveDialog(true)}
              className={compactBtn}
            >
              <Check className="h-3 w-3 shrink-0" strokeWidth={2.35} />
              <span className="truncate">
                {ct.actions.archiveSelectedMobile.replace(
                  "{count}",
                  String(selectedTests.size),
                )}
              </span>
            </ColoredOutlineButton>
          ) : (
            renderArchiveNavButton({ fullWidth: true, compact: true })
          )}
        </div>
      </div>
    );
  };

  /** Search + actions. Mobile: search only (archive/tabs live above/below). Desktop: full toolbar. */
  const renderListToolbar = (opts?: { submittedTab?: boolean }) => {
    if (isMobile) {
      return (
        <div className="mb-3 flex flex-col gap-2">
          <Input
            placeholder={ct.search.placeholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="backdrop-blur w-full text-[10px] sm:text-sm h-8"
          />
          <CarTestSortSelect
            value={testSort}
            onValueChange={(v) => {
              setTestSort(v);
              setCurrentPage(1);
            }}
          />
        </div>
      );
    }

    const showConfirm = isArchiveMode && selectedTests.size > 0;
    const actionBtnClass =
      "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap shrink-0 w-auto";

    return (
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
        <Input
          placeholder={ct.search.placeholder}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="backdrop-blur w-full text-[10px] sm:text-sm h-8 sm:h-10 md:min-w-0 md:flex-1"
        />
        <CarTestSortSelect
          value={testSort}
          onValueChange={(v) => {
            setTestSort(v);
            setCurrentPage(1);
          }}
          className="md:w-[200px] md:shrink-0"
        />
        <div className="flex gap-2 items-stretch w-auto shrink-0 flex-wrap justify-end">
          <ColoredOutlineButton
            type="button"
            variant="neutral"
            tintedBackground
            size="sm"
            onClick={toggleArchiveMode}
            className={actionBtnClass}
          >
            <ListChecks
              className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
              strokeWidth={2.35}
            />
            <span className="truncate">
              {archiveToggleLabel(opts?.submittedTab)}
            </span>
          </ColoredOutlineButton>

          {showConfirm ? (
            <ColoredOutlineButton
              type="button"
              variant="success"
              tintedBackground
              size="sm"
              onClick={() => setShowArchiveDialog(true)}
              className={actionBtnClass}
            >
              <Check
                className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                strokeWidth={2.35}
              />
              <span className="truncate">
                {ct.actions.archiveSelected.replace(
                  "{count}",
                  String(selectedTests.size),
                )}
              </span>
            </ColoredOutlineButton>
          ) : null}

          {renderArchiveNavButton()}
          <CreateTestButton onClick={handleCreateTest} />
        </div>
      </div>
    );
  };

  return (
    <div className={cn(isNativeApp && "native-car-test-page")}>
      <div className="container max-w-4xl mx-auto px-4">
        <div className="relative mb-4">
          {isMobile ? (
            <div className="flex justify-center items-center">
              <h1 className="text-xl font-bold tracking-tight">{ct.title}</h1>
            </div>
          ) : (
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <BtnNavigateBack
                label="Dashboard"
                className="mb-0 shrink-0 justify-self-start"
              />
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-center px-2">
                {ct.title}
              </h1>
              <div />
            </div>
          )}
        </div>
        {/* Non-admin: Mina tester | Inskickade */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value as "own" | "submitted");
              setSelectedTests(new Set());
              setIsArchiveMode(false);
              navigate(`/car-test?tab=${value}`, { replace: true });
            }}
            className="w-full"
          >
            {isMobile ? (
              <>
                {/* Secondary: Arkivera / Arkiv + Ny */}
                {renderMobileArchiveBar()}
                {/* Search */}
                {renderListToolbar()}
                {/* Main focus: larger pill tabs */}
                <div className="mb-3 flex w-full justify-center">
                  <MobilePillTabs
                    className="!mb-0 !mx-0 !w-max max-w-[min(100%,20rem)] [&_.native-scroll-chip]:!min-h-9 [&_.native-scroll-chip]:!h-9 [&_.native-scroll-chip]:!py-0 [&_.native-scroll-chip]:!px-3.5 [&_.native-scroll-chip]:!text-[13px] [&_.native-scroll-chip]:!font-semibold"
                    items={[
                      { value: "own", label: ct.tabs.own },
                      { value: "submitted", label: ct.tabs.submitted },
                    ]}
                    value={activeTab}
                    onChange={(value) => {
                      setActiveTab(value);
                      setSelectedTests(new Set());
                      setIsArchiveMode(false);
                      navigate(`/car-test?tab=${value}`, { replace: true });
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 mb-3 justify-center flex-wrap">
                <TabsList className="backdrop-blur rounded-xl h-8 p-0.5 text-xs">
                  <TabsTrigger
                    value="own"
                    className={`rounded-lg py-1 px-2.5 text-xs ${
                      isDark
                        ? "data-[state=active]:bg-accent/70"
                        : "data-[state=active]:bg-accent"
                    }`}
                  >
                    {ct.tabs.own}
                  </TabsTrigger>
                  <TabsTrigger
                    value="submitted"
                    className={`rounded-lg py-1 px-2.5 text-xs ${
                      isDark
                        ? "data-[state=active]:bg-accent/70"
                        : "data-[state=active]:bg-accent"
                    }`}
                  >
                    {ct.tabs.submitted}
                  </TabsTrigger>
                </TabsList>
              </div>
            )}

            <TabsContent value="own">
              {!isMobile && renderListToolbar()}
              <div className="space-y-2">
                <AnimatePresence>
                  {activeTests && activeTests.length > 0 ? (
                    activeTests.map((test) => (
                      <motion.div
                        key={test.id}
                        className={`relative ${isArchiveMode ? "cursor-pointer" : ""}`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 200 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => {
                          if (isArchiveMode) {
                            const newSelected = new Set(selectedTests);
                            if (newSelected.has(test.id)) {
                              newSelected.delete(test.id);
                            } else {
                              newSelected.add(test.id);
                            }
                            setSelectedTests(newSelected);
                          }
                        }}
                      >
                        <div
                          className={`flex items-start gap-3 ${isArchiveMode ? "mb-2" : ""}`}
                        >
                          {isArchiveMode && (
                            <input
                              type="checkbox"
                              checked={selectedTests.has(test.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newSelected = new Set(selectedTests);
                                if (e.target.checked) {
                                  newSelected.add(test.id);
                                } else {
                                  newSelected.delete(test.id);
                                }
                                setSelectedTests(newSelected);
                              }}
                              className="w-5 h-5 mt-2 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-1">
                            <ActiveTest
                              key={test.vehicleDetails.regNumber}
                              test={test}
                              onView={() => setViewTest(test)}
                              onEdit={handleEdit}
                              onComplete={handleCompleteTest}
                              onRequestChange={handleRequestChange}
                              onWithdrawReview={handleWithdrawReview}
                              isAdmin={false}
                              selectionMode={isArchiveMode}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {ct.messages.noOwnTests}
                    </div>
                  )}
                </AnimatePresence>
              </div>
              {activeTab === "own" && totalUserTests > 0 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalUserTests / itemsPerPage)}
                    totalItems={totalUserTests}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(next) => {
                      setItemsPerPage(next);
                      setCurrentPage(1);
                    }}
                    itemsPerPageOptions={[10, 25, 50]}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="submitted">
              {!isMobile && renderListToolbar({ submittedTab: true })}
              <div className="space-y-2">
                <AnimatePresence>
                  {submittedTests && submittedTests.length > 0 ? (
                    submittedTests.map((test) => (
                      <motion.div
                        key={test.id}
                        className={`relative ${isArchiveMode ? "cursor-pointer" : ""}`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 200 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => {
                          if (isArchiveMode) {
                            const newSelected = new Set(selectedTests);
                            if (newSelected.has(test.id)) {
                              newSelected.delete(test.id);
                            } else {
                              newSelected.add(test.id);
                            }
                            setSelectedTests(newSelected);
                          }
                        }}
                      >
                        <div
                          className={`flex items-start gap-3 ${isArchiveMode ? "mb-2" : ""}`}
                        >
                          {isArchiveMode && (
                            <input
                              type="checkbox"
                              checked={selectedTests.has(test.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newSelected = new Set(selectedTests);
                                if (e.target.checked) {
                                  newSelected.add(test.id);
                                } else {
                                  newSelected.delete(test.id);
                                }
                                setSelectedTests(newSelected);
                              }}
                              className="w-5 h-5 mt-2 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="flex-1">
                            <ActiveTest
                              key={test.vehicleDetails.regNumber}
                              test={test}
                              onView={() => setViewTest(test)}
                              onDownload={handleDownloadPdf}
                              onRequestChange={handleRequestChange}
                              onWithdrawReview={handleWithdrawReview}
                              isAdmin={false}
                              selectionMode={isArchiveMode}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {ct.messages.noSubmittedTests}
                    </div>
                  )}
                </AnimatePresence>
              </div>
              {activeTab === "submitted" && totalUserTests > 0 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalUserTests / itemsPerPage)}
                    totalItems={totalUserTests}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(next) => {
                      setItemsPerPage(next);
                      setCurrentPage(1);
                    }}
                    itemsPerPageOptions={[10, 25, 50]}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
      </div>

      <CreateTestDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      {/* Dialog för att redigera test (för complement status) */}
      {selectedTestForEdit && (
        <CreateTestDialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) setSelectedTestForEdit(null);
          }}
          initialData={{
            id: selectedTestForEdit.id,
            regNumber: selectedTestForEdit.vehicleDetails?.regNumber,
            vehicleDetails: selectedTestForEdit.vehicleDetails,
            vehicleTestData: selectedTestForEdit.vehicleTestData,
            images: selectedTestForEdit.images,
            status: selectedTestForEdit.status,
            price: selectedTestForEdit.price,
            comments: selectedTestForEdit.comments,
            vatCar: selectedTestForEdit.vatCar,
            purchasePrice: selectedTestForEdit.purchasePrice,
          }}
          isEditMode={true}
          title={ct.dialogs.edit.title.replace(
            "{vehicleName}",
            selectedTestForEdit.vehicleName,
          )}
          onTestSaved={() => {
            setIsEditDialogOpen(false);
            setSelectedTestForEdit(null);
            refetchUserTests();
          }}
        />
      )}

      {/* Dialog för att slutföra test (samma som EditDialog) */}
      {selectedTestForCompletion && (
        <CreateTestDialog
          open={isCompleteDialogOpen}
          onOpenChange={setIsCompleteDialogOpen}
          initialData={{
            id: selectedTestForCompletion.id,
            regNumber: selectedTestForCompletion.vehicleDetails?.regNumber,
            vehicleDetails: selectedTestForCompletion.vehicleDetails,
            vehicleTestData: selectedTestForCompletion.vehicleTestData,
            images: selectedTestForCompletion.images,
            status: selectedTestForCompletion.status,
            price: selectedTestForCompletion.price,
            comments: selectedTestForCompletion.comments,
            vatCar: selectedTestForCompletion.vatCar,
            purchasePrice: selectedTestForCompletion.purchasePrice,
          }}
          isEditMode={true}
          isCompletionMode={true}
          title={`Slutför: ${selectedTestForCompletion.vehicleName}`}
        />
      )}

      {/* Dialog för att begära ändring */}
      {selectedTestForChange && (
        <Dialog
          open={isRequestChangeDialogOpen}
          onOpenChange={() => {
            setIsRequestChangeDialogOpen(false);
            setSelectedTestForChange(null);
            setRequestChangeMessage("");
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{ct.dialogs.requestChange.title}</DialogTitle>
            </DialogHeader>
            <div className="mb-2 text-sm text-muted-foreground">
              {ct.dialogs.requestChange.description}
            </div>
            <Textarea
              className="w-full border bg-card rounded p-2 text-sm min-h-[120px]"
              placeholder={ct.dialogs.requestChange.placeholder}
              value={requestChangeMessage}
              onChange={(e) => setRequestChangeMessage(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsRequestChangeDialogOpen(false);
                  setSelectedTestForChange(null);
                  setRequestChangeMessage("");
                }}
              >
                {ct.dialogs.requestChange.cancel}
              </Button>
              <Button
                variant="default"
                onClick={handleSendChangeRequest}
                disabled={!requestChangeMessage.trim()}
              >
                {ct.dialogs.requestChange.send}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Visningsdialog för testprotokoll (samma som AdminCarTest) */}
      <Dialog open={!!viewTest} onOpenChange={() => setViewTest(null)}>
        <DialogContent
          expandable={isNativeApp}
          sheetOpen={!!viewTest}
          className={cn(
            "max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col p-0 sm:p-0",
            isNativeApp && "native-test-sheet native-sheet-expandable max-w-none w-full",
          )}
        >
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
            <DialogTitle>
              {viewTest
                ? [
                    viewTest.vehicleDetails?.make,
                    viewTest.vehicleDetails?.model,
                    viewTest.vehicleDetails?.year,
                  ]
                    .filter(Boolean)
                    .join(" ") || "Testprotokoll"
                : "Testprotokoll"}
            </DialogTitle>
          </DialogHeader>
          {viewTest && (
            <div className="px-4 sm:px-6 pb-6 overflow-y-auto flex-1 min-h-0">
              <VehicleTestDocumentView
                data={mapVehicleTestToDocumentData(viewTest)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Generation Dialog */}
      <PdfGenerationDialog
        isOpen={isGeneratingPdf.isOpen}
        testName={isGeneratingPdf.testName}
        fileName={isGeneratingPdf.fileName}
      />

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ct.dialogs.archive.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {ct.dialogs.archive.message
                .replace("{count}", String(selectedTests.size))
                .replace(
                  "{plural}",
                  selectedTests.size !== 1 ? ct.plural.tests : ct.plural.test,
                )}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveDialog(false);
                  setSelectedTests(new Set());
                }}
              >
                {ct.dialogs.archive.cancel}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await archiveCarTests(Array.from(selectedTests));
                    toast.success(
                      ct.messages.testsArchived
                        .replace("{count}", String(selectedTests.size))
                        .replace(
                          "{plural}",
                          selectedTests.size !== 1
                            ? ct.plural.tests
                            : ct.plural.test,
                        ),
                    );
                    setSelectedTests(new Set());
                    setShowArchiveDialog(false);
                  } catch (error) {
                    toast.error(ct.messages.archiveError);
                  }
                }}
                disabled={isArchivingCarTests || selectedTests.size === 0}
              >
                {isArchivingCarTests
                  ? ct.dialogs.archive.archiving
                  : ct.dialogs.archive.archive}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarTest;
